/**
 * Buy Simulator page - "What if I buy X shares of Y?"
 *
 * Accepts ?symbol= query param to pre-fill the symbol field.
 * Example: /what-if/buy?symbol=NVDA
 */
import { useState, useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useBuySimulator, usePrice } from '../hooks'
import { Input } from '../components/common'
import type { BuySimulatorResult } from '../types'

function formatCurrency(value: number | null | undefined): string {
  if (value === null || value === undefined) return '-'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(value)
}

function formatPercent(value: number | null | undefined): string {
  if (value === null || value === undefined) return '-'
  return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`
}

export function BuySimulatorPage() {
  const { calculate, holdings } = useBuySimulator()
  const [searchParams] = useSearchParams()

  // Get symbol from query param
  const initialSymbol = searchParams.get('symbol')?.toUpperCase() || ''

  // Form state
  const [symbol, setSymbol] = useState(initialSymbol)
  const [symbolToLookup, setSymbolToLookup] = useState<string | null>(initialSymbol || null)
  const [shares, setShares] = useState<number>(0)
  const [pricePerShare, setPricePerShare] = useState<number>(0)
  const [useCurrentPrice, setUseCurrentPrice] = useState(true)

  // Price lookup - only triggers on blur, not every keystroke
  const { data: priceData, isLoading: priceLoading } = usePrice(symbolToLookup)

  // Trigger lookup when symbol input loses focus
  const handleSymbolBlur = () => {
    if (symbol.length >= 1) {
      setSymbolToLookup(symbol.toUpperCase())
    } else {
      setSymbolToLookup(null)
    }
  }

  // Update price when lookup completes
  useEffect(() => {
    if (priceData && useCurrentPrice) {
      setPricePerShare(priceData.price)
    }
  }, [priceData, useCurrentPrice])

  // Calculate result
  const result: BuySimulatorResult | null = useMemo(() => {
    if (!symbol || shares <= 0 || pricePerShare <= 0) return null
    return calculate({ symbol, shares, pricePerShare })
  }, [symbol, shares, pricePerShare, calculate])

  // Check if we already hold this symbol
  const existingHolding = useMemo(() => {
    return holdings?.find(h => h.symbol.toUpperCase() === symbol.toUpperCase())
  }, [holdings, symbol])

  const portfolioValue = holdings?.reduce((sum, h) =>
    sum + (h.current_value ?? h.cost_basis), 0
  ) ?? 0

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Buy Simulator</h1>
        <p className="mt-1 text-sm text-gray-500">
          See how a new purchase would affect your portfolio allocation
        </p>
      </div>

      {/* Input form */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <Input
              label="Symbol"
              placeholder="AAPL"
              value={symbol}
              onChange={(e) => setSymbol(e.target.value.toUpperCase())}
              onBlur={handleSymbolBlur}
            />
            {priceLoading && (
              <p className="mt-1 text-xs text-gray-400">Loading price...</p>
            )}
            {priceData && (
              <p className="mt-1 text-xs text-gray-500">
                Current: {formatCurrency(priceData.price)}
                <span className="text-gray-400 ml-1">
                  ({priceData.source === 'snapshot' ? 'from snapshot' : 'live'})
                </span>
              </p>
            )}
          </div>

          <div>
            <Input
              label="Shares"
              type="number"
              min={0}
              step={1}
              value={shares || ''}
              onChange={(e) => setShares(parseInt(e.target.value) || 0)}
            />
          </div>

          <div>
            <Input
              label="Price per Share"
              type="number"
              min={0}
              step={0.01}
              value={pricePerShare || ''}
              onChange={(e) => {
                setPricePerShare(parseFloat(e.target.value) || 0)
                setUseCurrentPrice(false)
              }}
            />
            {priceData && !useCurrentPrice && (
              <button
                type="button"
                onClick={() => {
                  setPricePerShare(priceData.price)
                  setUseCurrentPrice(true)
                }}
                className="mt-1 text-xs text-blue-600 hover:text-blue-800"
              >
                Use current price
              </button>
            )}
          </div>
        </div>

        {/* Quick calculations */}
        {shares > 0 && pricePerShare > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex gap-6 text-sm">
              <div>
                <span className="text-gray-500">Total Cost:</span>{' '}
                <span className="font-medium">{formatCurrency(shares * pricePerShare)}</span>
              </div>
              <div>
                <span className="text-gray-500">% of Portfolio:</span>{' '}
                <span className="font-medium">
                  {portfolioValue > 0
                    ? ((shares * pricePerShare) / portfolioValue * 100).toFixed(2)
                    : 0
                  }%
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Existing holding info */}
      {existingHolding && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="font-medium text-blue-900">You already hold {symbol}</h3>
          <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-blue-700">Current Shares:</span>{' '}
              <span className="font-medium text-blue-900">{existingHolding.shares}</span>
            </div>
            <div>
              <span className="text-blue-700">Current Value:</span>{' '}
              <span className="font-medium text-blue-900">
                {formatCurrency(existingHolding.current_value)}
              </span>
            </div>
            <div>
              <span className="text-blue-700">Cost Basis:</span>{' '}
              <span className="font-medium text-blue-900">
                {formatCurrency(existingHolding.cost_basis)}
              </span>
            </div>
            <div>
              <span className="text-blue-700">Gain/Loss:</span>{' '}
              <span className={`font-medium ${
                (existingHolding.gain_loss ?? 0) >= 0 ? 'text-green-700' : 'text-red-700'
              }`}>
                {formatCurrency(existingHolding.gain_loss)} ({formatPercent(existingHolding.gain_loss_pct)})
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Result */}
      {result && (
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Impact Analysis</h2>
          </div>

          <div className="p-6 space-y-6">
            {/* Before/After comparison */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Current State */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-700 mb-3">Current</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Portfolio Value</span>
                    <span className="font-medium">{formatCurrency(result.currentPortfolioValue)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">{result.symbol} Value</span>
                    <span className="font-medium">{formatCurrency(result.currentSymbolValue)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">{result.symbol} Allocation</span>
                    <span className="font-medium">{result.currentSymbolAllocation.toFixed(2)}%</span>
                  </div>
                </div>
              </div>

              {/* Transaction */}
              <div className="bg-blue-50 rounded-lg p-4 flex flex-col items-center justify-center">
                <div className="text-3xl text-blue-600 mb-2">+</div>
                <div className="text-center">
                  <div className="font-medium text-blue-900">
                    {result.shares} shares
                  </div>
                  <div className="text-sm text-blue-700">
                    @ {formatCurrency(result.pricePerShare)}
                  </div>
                  <div className="font-bold text-blue-900 mt-1">
                    = {formatCurrency(result.totalCost)}
                  </div>
                </div>
              </div>

              {/* After State */}
              <div className="bg-green-50 rounded-lg p-4">
                <h3 className="text-sm font-medium text-green-700 mb-3">After</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-green-600">Portfolio Value</span>
                    <span className="font-medium text-green-900">
                      {formatCurrency(result.newPortfolioValue)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-green-600">{result.symbol} Value</span>
                    <span className="font-medium text-green-900">
                      {formatCurrency(result.newSymbolValue)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-green-600">{result.symbol} Allocation</span>
                    <span className="font-medium text-green-900">
                      {result.newSymbolAllocation.toFixed(2)}%
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Impact summary */}
            <div className="border-t border-gray-200 pt-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-gray-700 mb-2">Allocation Change</h3>
                  <div className="text-2xl font-bold text-gray-900">
                    {result.allocationChange >= 0 ? '+' : ''}
                    {result.allocationChange.toFixed(2)} pp
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    {result.symbol} weight changes from {result.currentSymbolAllocation.toFixed(1)}%
                    to {result.newSymbolAllocation.toFixed(1)}%
                  </p>
                </div>

                <div className={`rounded-lg p-4 ${
                  result.diversificationImpact === 'increases'
                    ? 'bg-green-50'
                    : result.diversificationImpact === 'decreases'
                    ? 'bg-yellow-50'
                    : 'bg-gray-50'
                }`}>
                  <h3 className={`text-sm font-medium mb-2 ${
                    result.diversificationImpact === 'increases'
                      ? 'text-green-700'
                      : result.diversificationImpact === 'decreases'
                      ? 'text-yellow-700'
                      : 'text-gray-700'
                  }`}>
                    Diversification Impact
                  </h3>
                  <div className={`text-2xl font-bold ${
                    result.diversificationImpact === 'increases'
                      ? 'text-green-900'
                      : result.diversificationImpact === 'decreases'
                      ? 'text-yellow-900'
                      : 'text-gray-900'
                  }`}>
                    {result.diversificationImpact === 'increases' && 'Improves'}
                    {result.diversificationImpact === 'decreases' && 'Reduces'}
                    {result.diversificationImpact === 'neutral' && 'Neutral'}
                  </div>
                  <p className={`text-sm mt-1 ${
                    result.diversificationImpact === 'increases'
                      ? 'text-green-600'
                      : result.diversificationImpact === 'decreases'
                      ? 'text-yellow-600'
                      : 'text-gray-500'
                  }`}>
                    {result.diversificationImpact === 'increases' && 'Adding a new or underweight position'}
                    {result.diversificationImpact === 'decreases' && 'Increasing concentration in top holding'}
                    {result.diversificationImpact === 'neutral' && 'Minimal impact on portfolio balance'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Empty state */}
      {!result && (
        <div className="bg-gray-50 rounded-lg p-12 text-center">
          <p className="text-gray-500">
            Enter a symbol, shares, and price to see the impact on your portfolio
          </p>
        </div>
      )}
    </div>
  )
}
